import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import toast from 'react-hot-toast';

const MoveTransactionModal = ({ visible, onCancel, transaction }) => {
  const { portfolios, addTransaction } = useData();
  const [destinationPortfolioId, setDestinationPortfolioId] = useState('');

  useEscapeKey(onCancel, visible);

  // Get source portfolio info
  const sourcePortfolio = portfolios.find(p => p.id === transaction?.portfolioId);
  const destinationPortfolio = portfolios.find(p => p.id === destinationPortfolioId);

  useEffect(() => {
    if (visible && transaction) {
      setDestinationPortfolioId('');
    }
  }, [visible, transaction]);

  const handleMove = () => {
    if (!destinationPortfolioId) {
      toast.error('Please select a destination account');
      return;
    }

    if (destinationPortfolioId === transaction.portfolioId) {
      toast.error('Destination account must be different from source account');
      return;
    }

    const amount = Math.abs(transaction.amount);

    try {
      addTransaction({
        type: 'withdrawal',
        portfolioId: transaction.portfolioId,
        amount: amount,
        date: transaction.date,
        description: `Transfer to ${destinationPortfolio.name}${transaction.description ? ` - ${transaction.description}` : ''}`,
        from: transaction.from || null,
        goalId: transaction.goalId || null,
        costBasis: null,
      });

      addTransaction({
        type: 'contribution',
        portfolioId: destinationPortfolioId,
        amount: amount,
        date: transaction.date,
        description: `Transfer from ${sourcePortfolio.name}${transaction.description ? ` - ${transaction.description}` : ''}`,
        from: transaction.from || null,
        goalId: transaction.goalId || null,
        costBasis: transaction.costBasis || null,
      });

      toast.success(`Transaction moved: ${sourcePortfolio.name} → ${destinationPortfolio.name}`);
      onCancel();
    } catch (error) {
      console.error('Error moving transaction:', error);
      toast.error('Failed to move transaction');
    }
  };

  if (!transaction) return null;

  return (
    <Transition appear show={visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onCancel}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900 mb-2">
                  Move Transaction
                </Dialog.Title>

                <hr className="mb-4 border-gray-200" />

                {/* Transaction Info */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Transaction Details</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-medium">{transaction.date}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Category:</span>
                      <span className="font-medium">{transaction.category}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount:</span>
                      <span className={`font-medium ${transaction.type === 'inflow' ? 'text-green-600' : 'text-red-600'}`}>
                        {transaction.type === 'inflow' ? '+' : '-'}
                        {formatCurrency(Math.abs(transaction.amount), transaction.currency, 2)}
                      </span>
                    </div>
                    {transaction.memo && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Memo:</span>
                        <span className="font-medium">{transaction.memo}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Move From/To */}
                <div className="mb-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Account
                      </label>
                      <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm">
                        {sourcePortfolio?.name || 'Unknown'}
                      </div>
                    </div>
                    <ArrowRightIcon className="w-6 h-6 text-gray-400 flex-shrink-0 mt-6" />
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        To Account <span className="text-red-600">*</span>
                      </label>
                      <select
                        value={destinationPortfolioId}
                        onChange={(e) => setDestinationPortfolioId(e.target.value)}
                        className="block w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">-- Select Account --</option>
                        {portfolios
                          .filter(p => p.id !== transaction.portfolioId)
                          .map(portfolio => (
                            <option key={portfolio.id} value={portfolio.id}>
                              {portfolio.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleMove}
                    disabled={!destinationPortfolioId}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: destinationPortfolioId ? '#012E58' : '#6b7280' }}
                  >
                    Move Transaction
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MoveTransactionModal;
